import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { loadTeamCatalog, TeamCatalog } from '@context/TeamCatalog.context'
import { useNotificationToast } from '@context/NotificationToastContext'
import { campaignSocket, chatSocket } from '@services/websocket/socket'
import { fetchAllCampaigns, fetchAllConversations } from '@store/actions'
import { updateSmsSendStatus } from '@store/reducers/chat.reducer'
import {
  createFailedToast,
  createNewChatToast,
} from '@utils/notificationToast.util'
import { getFullName } from '@utils/fullname.util'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import { findSmsConversation } from '@utils/smsConversation.util'

interface IncomingSmsEvent {
  messageSid: string
  sender: string
  receiver: string
  body: string
  dbName: string | null
  clinicId: string | null
  assignedTeam: string | null
  conversationId?: string | null
}

interface SmsStatusEvent {
  message_sid: string
  status: string
}

const viewerTeamForRole = (
  workRole: string | null | undefined,
  catalog: TeamCatalog | null,
): string | null => {
  if (!workRole || !catalog) return null
  return Object.prototype.hasOwnProperty.call(catalog.roleToTeam, workRole)
    ? catalog.roleToTeam[workRole]
    : catalog.defaultTeam
}

const smsVisibleToViewer = (
  assignedTeam: string | null | undefined,
  viewerTeam: string | null,
): boolean => {
  if (!assignedTeam) return true
  if (viewerTeam === 'Admin') return true
  return assignedTeam === viewerTeam
}

const ChatSocketListener: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { showToast } = useNotificationToast()
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated)
  const dbName = useAppSelector((state) => state.auth.dbName)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const workRole = useAppSelector(
    (state) => state.chatInternal.loggedInUser?.provider_profile?.work_role,
  )
  const [teamCatalog, setTeamCatalog] = useState<TeamCatalog | null>(null)
  const conversations = useAppSelector((state) => state.chat.data)
  const current = useRef({
    isAuthenticated,
    dbName,
    clinicId: selectedClinic?.clinic_id,
    conversations,
  })
  current.current = {
    isAuthenticated,
    dbName,
    clinicId: selectedClinic?.clinic_id,
    conversations,
  }

  useEffect(() => {
    void loadTeamCatalog()
      .then(setTeamCatalog)
      .catch(() => setTeamCatalog(null))
  }, [])

  useEffect(() => {
    const handleIncomingSms = (smsData: IncomingSmsEvent): void => {
      console.info('(Chat websocket) Incoming-sms.')

      if (!isAuthenticated || !dbName) {
        console.info('(Chat websocket) User not authenticated.')
        return
      }

      if (smsData.dbName && smsData.dbName !== dbName) {
        console.info('(Chat websocket) Tenant mismatch.')
        return
      }

      if (!selectedClinic) {
        console.info('(Chat websocket) No clinic selected.')
        return
      }

      if (smsData.clinicId && smsData.clinicId !== selectedClinic.clinic_id) {
        console.info('(Chat websocket) Clinic mismatch.')
        return
      }

      if (
        !smsVisibleToViewer(
          smsData.assignedTeam,
          viewerTeamForRole(workRole, teamCatalog),
        )
      ) {
        return
      }

      const conversation = findSmsConversation(
        current.current.conversations ?? [],
        smsData,
      )
      const clinicId = selectedClinic.clinic_id
      const stillInScope = (): boolean =>
        current.current.isAuthenticated &&
        current.current.dbName === dbName &&
        current.current.clinicId === clinicId
      const openConversation = async (): Promise<void> => {
        if (!stillInScope()) {
          showToast(
            createFailedToast(
              'Open chat',
              'Switch back to the clinic that received this SMS to open its conversation.',
            ),
          )
          return
        }
        try {
          const refreshed = await dispatch(fetchAllConversations()).unwrap()
          if (!stillInScope()) return
          const target = findSmsConversation(refreshed ?? [], smsData)
          if (!target?.chat_conversation_id)
            throw new Error('Conversation unavailable')
          navigate('/message', {
            state: { tab: 'chat', conversationId: target.chat_conversation_id },
          })
        } catch {
          if (stillInScope())
            showToast(
              createFailedToast(
                'Could not open chat',
                'The SMS conversation could not be loaded. Please try again.',
              ),
            )
        }
      }

      const senderDigits = normalizePhoneNumber(smsData.sender)
      const matchingParticipant = conversation?.chat_participant?.find(
        (participant) =>
          participant.phone_number &&
          normalizePhoneNumber(participant.phone_number) === senderDigits,
      )
      const contactName = matchingParticipant?.contact
        ? getFullName(matchingParticipant.contact)
        : ''
      const senderLabel = contactName || smsData.sender

      showToast({
        ...createNewChatToast(
          `New message from ${senderLabel}`,
          smsData.body,
          'mgc_message_4_fill',
        ),
        actionButtonText: 'Open chat',
        onAction: () => {
          void openConversation()
        },
      })
      // Refresh unread counts without delaying the visible snackbar and its action.
      void dispatch(fetchAllConversations())
    }

    const handleSmsStatus = (smsStatus: SmsStatusEvent): void => {
      console.info(
        `(Chat websocket) ${smsStatus.message_sid} status: ${smsStatus.status}`,
      )
      dispatch(updateSmsSendStatus(smsStatus))
    }

    const handleCampaignMessageStatus = (smsStatus: SmsStatusEvent): void => {
      console.info(
        `(Campaign websocket) ${smsStatus.message_sid} status: ${smsStatus.status}`,
      )
      dispatch(fetchAllCampaigns())
    }

    chatSocket.on('incoming-sms', handleIncomingSms)
    chatSocket.on('sms-status', handleSmsStatus)
    campaignSocket.on('campaign-message-status', handleCampaignMessageStatus)

    return () => {
      chatSocket.off('incoming-sms', handleIncomingSms)
      chatSocket.off('sms-status', handleSmsStatus)
      campaignSocket.off('campaign-message-status', handleCampaignMessageStatus)
    }
  }, [
    dispatch,
    showToast,
    navigate,
    isAuthenticated,
    dbName,
    selectedClinic,
    workRole,
    teamCatalog,
  ])

  return null
}

export default ChatSocketListener
